import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/primitives";
import { CAPABILITIES } from "@/data/content";
import { cn } from "@/lib/utils";

export default function CapabilitiesSection({
    heading = "Capabilities built around operations",
    intro = "Not services. Capabilities we combine to remove repetitive work wherever it hides in your operation.",
    className,
}) {
    return (
        <section className={cn("border-t border-border py-24 md:py-32", className)}>
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow>Solutions</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 max-w-2xl text-balance text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl">
                                {heading}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <Reveal delay={0.1}>
                            <p className="text-lg leading-relaxed text-muted-foreground">
                                {intro}
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {CAPABILITIES.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="group flex h-full flex-col rounded-3xl border border-border bg-card p-8 transition-colors duration-300 hover:border-foreground/25"
                                >
                                    <div className="mb-6 grid h-12 w-12 place-items-center rounded-xl border border-border bg-background text-navy transition-colors duration-300 group-hover:border-navy/40">
                                        <Icon
                                            className="h-5 w-5"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <h3 className="text-xl font-semibold tracking-tight">
                                        {c.title}
                                    </h3>
                                    <p className="mt-3 flex-1 text-[15px] leading-relaxed text-muted-foreground">
                                        {c.desc}
                                    </p>
                                    <Link
                                        to="/contact"
                                        data-testid={`capability-learn-${c.slug}`}
                                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground"
                                    >
                                        Learn More
                                        <ArrowUpRight
                                            className="h-4 w-4 text-navy transition-transform duration-300 ease-premium group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            strokeWidth={2}
                                        />
                                    </Link>
                                </motion.div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
